import { Controller } from "@hotwired/stimulus";
import Dropzone from "dropzone";

Dropzone.autoDiscover = false;

// File-manager upload modal. Files are staged into Shrine's `cache` storage — posted
// straight to S3 with a presigned POST in staging/production, or through Shrine's mounted
// upload endpoint locally — and only once the whole queue has landed do we POST the cached
// file descriptors to Storage::FilesController#create, which turns them into
// Storage::File + Storage::Upload records.
export default class extends Controller {
  static targets = [
    "dropArea",
    "previews",
    "previewTemplate",
    "empty",
    "header",
    "headline",
    "cancelButton",
    "addButton",
    "totalBar",
    "selectedFooter",
    "uploadingFooter",
    "abortButton",
    "completeFooter",
    "uploadButton",
    "uploadButtonLabel",
    "progressHeadline",
    "progressDetail",
    "saveError",
    "completeNote",
    "completeIcon",
  ];

  static values = {
    uploadUrl: String, // Shrine presign endpoint (S3) or upload endpoint (local)
    createUrl: String,
    parentId: String,
    directToS3: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 10 },
    maxFilesize: { type: Number, default: 50 * 1024 * 1024 },
    extensionIcons: Object,
    defaultIcon: String,
  };

  connect() {
    // Descriptors staged into Shrine's cache but not yet saved. Cleared once #create
    // accepts them, so a later batch or a retry can never re-post a saved descriptor.
    this.pending = [];
    this.presigning = 0;
    this.saveError = null;
    this.state = "empty";
    this.initializeDropzone();
    this.render();
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy();
      this.dropzone = null;
    }
  }

  initializeDropzone() {
    this.dropzone = new Dropzone(this.dropAreaTarget, {
      url: (files) => (this.directToS3Value ? files[0].presignUrl : this.uploadUrlValue),
      method: "post",
      paramName: "file", // Shrine's upload endpoint and S3's POST policy both expect "file"
      params: (files) => files[0].presignFields || {},
      // Dropzone measures maxFilesize in MiB, so the byte limit has to be converted or a
      // 50 MB cap silently becomes a 50 TB one.
      maxFilesize: this.maxFilesizeValue / (1024 * 1024),
      maxFiles: this.maxFilesValue,
      autoProcessQueue: false,
      addRemoveLinks: false,
      uploadMultiple: false,
      createImageThumbnails: true,
      thumbnailWidth: 160,
      thumbnailHeight: 160,
      previewsContainer: this.previewsTarget,
      previewTemplate: this.previewTemplateTarget.innerHTML,
      // A presigned POST to S3 is cross-origin, and the Cache-Control and X-Requested-With
      // headers Dropzone sends by default are not CORS-safelisted — they would force a
      // preflight the bucket does not answer.
      defaultHeaders: !this.directToS3Value,
      headers: this.directToS3Value ? {} : { "X-CSRF-Token": this.csrfToken },
      accept: (file, done) => this.prepareUpload(file, done),
    });

    this.dropzone.on("addedfile", (file) => this.fileAdded(file));
    this.dropzone.on("removedfile", () => {
      if (this.dropzone.files.length === 0 && this.state === "selected") this.state = "empty";
      this.render();
    });
    this.dropzone.on("thumbnail", (file) => this.thumbnailReady(file));
    this.dropzone.on("processing", (file) => this.fileProcessing(file));
    this.dropzone.on("totaluploadprogress", () => this.progressChanged());
    this.dropzone.on("success", (file) => this.fileUploaded(file));
    this.dropzone.on("error", (file) => this.fileFailed(file));
    this.dropzone.on("queuecomplete", () => this.queueComplete());
    this.dropzone.on("maxfilesexceeded", (file) => {
      this.dropzone.removeFile(file);
      alert(`You can upload up to ${this.maxFilesValue} files at a time.`);
    });
  }

  // Runs before a file enters the upload queue, which is where its presigned POST gets
  // fetched — calling `done()` is what releases the file to be uploaded.
  async prepareUpload(file, done) {
    if (!this.directToS3Value) {
      done();
      return;
    }

    this.presigning += 1;
    this.render();

    try {
      const query = new URLSearchParams({ filename: file.name, type: file.type || "" });
      const response = await fetch(`${this.uploadUrlValue}?${query}`);

      if (!response.ok) throw new Error(`Presign failed (${response.status})`);

      const { url, fields } = await response.json();
      file.presignUrl = url;
      file.presignFields = fields;
      file.cacheId = fields.key.replace(/^uploads\/cache\//, "");
      done();
    } catch (error) {
      done("Could not prepare this file for upload. Please try again.");
    } finally {
      this.presigning -= 1;
      this.render();
    }
  }

  fileAdded(file) {
    this.setFileIcon(file);
    this.setFileSize(file);

    // A file dropped after a batch finished (or after a failed save) starts a new
    // selection, rather than sitting behind a footer that offers no way to send it.
    if (["empty", "complete", "failed"].includes(this.state)) this.state = "selected";
    this.render();
  }

  thumbnailReady(file) {
    const thumbnail = file.previewElement.querySelector("[data-dz-thumbnail]");
    const icon = file.previewElement.querySelector("[data-dz-icon]");

    if (thumbnail) thumbnail.classList.remove("d-none");
    if (icon) icon.classList.add("d-none");
  }

  fileProcessing(file) {
    this.toggle(file, "[data-dz-overlay]", true);
  }

  fileUploaded(file) {
    this.pending.push(this.cachedFileData(file));
    this.toggle(file, "[data-dz-overlay]", false);
    this.toggle(file, "[data-dz-success]", true);
    this.toggle(file, "[data-dz-remove]", false);
    this.render();
  }

  fileFailed(file) {
    this.toggle(file, "[data-dz-overlay]", false);
    this.render();
  }

  // The descriptor Storage::FilesController#create expects: a Shrine cached-file hash. S3
  // answers a presigned POST with XML rather than JSON, so it is rebuilt from the key we
  // already signed for; Shrine's upload endpoint returns one directly under `data`.
  cachedFileData(file) {
    if (this.directToS3Value) {
      return {
        id: file.cacheId,
        storage: "cache",
        metadata: {
          filename: file.name,
          size: file.size,
          mime_type: file.type,
        },
      };
    }

    return JSON.parse(file.xhr.responseText).data;
  }

  browse() {
    this.dropzone.hiddenFileInput.click();
  }

  upload() {
    const queued = this.dropzone.getQueuedFiles();

    if (queued.length > 0) {
      this.state = "uploading";
      this.saveError = null;
      this.totalBytes = queued.reduce((sum, file) => sum + file.size, 0);
      this.startedAt = performance.now();
      this.render();

      this.dropzone.options.autoProcessQueue = true;
      this.dropzone.processQueue();
      return;
    }

    // Nothing left to send, so this is a retry of a save that failed: the files are all
    // already in Shrine's cache and only #create still has to accept them.
    if (this.pending.length > 0) this.save();
  }

  // State is reset before the files are dropped: removeAllFiles aborts in-flight uploads,
  // and the `queuecomplete` that follows must not be mistaken for a finished batch.
  cancel() {
    if (this.state === "saving") return;

    this.dropzone.options.autoProcessQueue = false;
    this.state = "empty";
    this.pending = [];
    this.saveError = null;
    this.dropzone.removeAllFiles(true);
    this.render();
  }

  done() {
    window.location.reload();
  }

  async queueComplete() {
    if (this.state !== "uploading") return;

    this.dropzone.options.autoProcessQueue = false;

    if (this.pending.length === 0) {
      this.state = this.dropzone.files.length === 0 ? "empty" : "selected";
      this.render();
      return;
    }

    await this.save();
  }

  async save() {
    this.state = "saving";
    this.saveError = null;
    this.render();

    let response;

    try {
      response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          parent_id: this.parentIdValue,
          files: this.pending,
        }),
      });

      if (!response.ok) {
        // A 500 rendering HTML, or a proxy error page, is not JSON — parsing it unguarded
        // would surface a SyntaxError to the user in place of the real message.
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `Files could not be saved (${response.status})`);
      }

      this.pending = [];
      // Files added while the save was in flight can still be sent; autoProcessQueue was
      // switched off when the batch drained, so they need the Upload button again.
      this.state = this.dropzone.getQueuedFiles().length > 0 ? "selected" : "complete";
    } catch (error) {
      if (response) {
        this.saveError = error.message || "Files could not be saved. Please try again.";
        this.state = "failed";
      } else {
        this.pending = [];
        this.state = "unconfirmed";
      }
    }

    this.render();
  }

  progressChanged() {
    if (this.state !== "uploading") return;

    this.render();
  }

  render() {
    if (!this.dropzone) return;

    const files = this.dropzone.files;
    // Files Dropzone rejected (oversize, wrong type) stay in the list so the reason is
    // visible, but they are never sent — so they must not inflate any count.
    const sendable = files.filter((file) => file.status !== Dropzone.ERROR);
    const queued = this.dropzone.getQueuedFiles().length;
    const failed = files.length - sendable.length;
    const retrying = queued === 0 && this.presigning === 0 && this.pending.length > 0;

    this.emptyTarget.classList.toggle("d-none", files.length > 0);
    // The drop copy is the body message's job; the header only labels a real selection.
    this.headerTarget.classList.toggle("d-none", files.length === 0);
    this.cancelButtonTarget.classList.toggle("d-none", !["selected", "failed"].includes(this.state));
    this.addButtonTarget.classList.toggle("d-none", ["saving", "complete", "unconfirmed"].includes(this.state));

    this.selectedFooterTarget.classList.toggle(
      "d-none",
      this.state !== "selected" && this.state !== "failed"
    );
    this.uploadingFooterTarget.classList.toggle(
      "d-none",
      this.state !== "uploading" && this.state !== "saving"
    );
    this.abortButtonTarget.classList.toggle("d-none", this.state === "saving");
    this.completeFooterTarget.classList.toggle("d-none", !["complete", "unconfirmed"].includes(this.state));

    this.headlineTarget.textContent = this.headlineText(sendable.length, failed);
    this.uploadButtonTarget.disabled = this.presigning > 0 || (queued === 0 && this.pending.length === 0);
    this.uploadButtonLabelTarget.textContent = retrying
      ? "Retry save"
      : `Upload ${pluralize(queued || sendable.length, "file")}`;

    this.saveErrorTarget.textContent = this.saveError || "";
    this.saveErrorTarget.classList.toggle("d-none", !this.saveError);

    this.renderComplete(sendable.length, failed);
    this.renderProgress(sendable, this.pending.length);
  }

  headlineText(count, failed) {
    switch (this.state) {
      case "uploading":
        return `Uploading ${pluralize(count, "file")}`;
      case "saving":
        return "Finishing up…";
      case "failed":
        return "Files could not be saved";
      case "unconfirmed":
        return "Save not confirmed";
      case "complete":
        return failed === 0 ? "Upload complete" : `Uploaded ${count} of ${count + failed} files`;
      default:
        return `${pluralize(count, "file")} selected`;
    }
  }

  // A batch where some files never made it is not a plain success, whatever the headline says.
  renderComplete(saved, failed) {
    if (!["complete", "unconfirmed"].includes(this.state)) return;

    const clean = this.state === "complete" && failed === 0;

    this.completeNoteTarget.textContent = this.completeNoteText(saved, failed);
    this.completeIconTarget.classList.toggle("text-success", clean);
    this.completeIconTarget.classList.toggle("text-warning", !clean);
  }

  completeNoteText(saved, failed) {
    if (this.state === "unconfirmed") return "Lost connection while saving. Reload to check before uploading again.";
    if (failed === 0) return "Complete";

    return `${pluralize(saved, "file")} saved · ${failed} failed`;
  }

  renderProgress(sendable, uploaded) {
    if (["complete", "unconfirmed"].includes(this.state)) {
      this.totalBarTarget.style.width = "100%";
      return;
    }

    if (this.state !== "uploading" && this.state !== "saving") {
      this.totalBarTarget.style.width = "0%";
      return;
    }

    const total = this.totalBytes || 0;
    const sent = Math.min(
      sendable.reduce((sum, file) => sum + (file.upload?.bytesSent || 0), 0),
      total
    );
    const percent = total === 0 ? 0 : Math.round((sent / total) * 100);

    this.totalBarTarget.style.width = `${percent}%`;
    this.progressHeadlineTarget.textContent =
      this.state === "saving" ? "Saving files…" : `Uploading: ${percent}%`;
    this.progressDetailTarget.textContent = [
      `${uploaded} of ${sendable.length} files uploaded`,
      `${formatSize(sent)} of ${formatSize(total)}`,
      this.remainingText(sent, total),
    ]
      .filter(Boolean)
      .join(" · ");
  }

  remainingText(sent, total) {
    if (this.state === "saving" || sent === 0 || sent >= total) return null;

    const elapsed = (performance.now() - this.startedAt) / 1000;
    if (elapsed < 0.5) return null;

    const seconds = Math.round(((total - sent) / (sent / elapsed)));
    if (!Number.isFinite(seconds)) return null;

    return `${formatDuration(seconds)} left`;
  }

  // Swaps the template's placeholder icon for the one the file manager will show the file
  // as once it is saved. Font Awesome rewrites the <i> into an <svg> from a mutation
  // observer, which runs after this handler, so it picks up the class set here.
  setFileIcon(file) {
    const icon = file.previewElement.querySelector("[data-dz-icon]");
    if (!icon) return;

    const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";

    icon.classList.remove(`fa-${this.defaultIconValue}`);
    const iconName = Object.hasOwn(this.extensionIconsValue, extension)
      ? this.extensionIconsValue[extension]
      : this.defaultIconValue;
    icon.classList.add(`fa-${iconName}`);
  }

  // Dropzone fills [data-dz-size] with markup of its own (`<strong>1.2</strong> MB`), which
  // reads as bold body text in this layout.
  setFileSize(file) {
    const size = file.previewElement.querySelector("[data-dz-size]");
    if (size) size.textContent = formatSize(file.size);
  }

  toggle(file, selector, visible) {
    const element = file.previewElement?.querySelector(selector);
    if (element) element.classList.toggle("d-none", !visible);
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}

function pluralize(count, noun) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function formatSize(bytes) {
  if (!bytes) return "0 KB";

  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);

  return `${(bytes / 1024 ** exponent).toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);

  return `${minutes}m ${seconds % 60}s`;
}
